/**
 * Pure helper functions for transforming parsing results.
 * Extracted from useParsingWorkflow.ts (Phase 2 refactoring).
 *
 * These functions have NO state, NO side effects (only console.log),
 * and can be safely tested in isolation.
 */

import type { DomainWithAnalysis } from '../types'

interface RawUrl {
  url?: string
  full_url?: string
  domain?: string
  total_clicks?: number
  queries_count?: number
  average_position?: number
  popularity?: string
  [key: string]: unknown
}

interface TransformedUrl extends RawUrl {
  full_url: string
  domain: string
  path: string
}

/**
 * Normalize raw URL objects from parsing result:
 * - guarantees `full_url` field
 * - extracts `domain` from full URL if missing
 * - extracts `path` for URL-type sources
 */
export function transformUrls(urls: RawUrl[]): TransformedUrl[] {
  return urls.map((u) => {
    const fullUrl = u.url || u.full_url || ''
    let domain = u.domain || ''
    let path = ''

    try {
      const urlObj = new URL(fullUrl.startsWith('http') ? fullUrl : `https://${fullUrl}`)
      domain = domain || urlObj.hostname.replace(/^www\./, '')
      path = urlObj.pathname + urlObj.search + urlObj.hash
      if (path === '/') path = ''
    } catch {
      // Keep original values if URL parsing fails
    }

    return {
      ...u,
      full_url: fullUrl,
      domain,
      path: path || '/',
    }
  })
}

/**
 * Combine domains and transformed URLs from a parsing result into a unified
 * `DomainWithAnalysis[]` list. Preserves separation between domain-type and
 * URL-type sources (no deduplication).
 */
export function formatDomainsFromResult(
  resultData: { domains?: Array<Partial<DomainWithAnalysis> & { host: string; full_url?: string }> },
  transformedUrls: TransformedUrl[]
): DomainWithAnalysis[] {
  // Format domains (pure domains) - source_type = 'domain'
  const domainsFromResult: DomainWithAnalysis[] = (resultData.domains || []).map((d) => ({
    ...d,
    host: d.host,
    source_type: 'domain' as const,
    full_url: d.full_url || `https://${d.host}/`,
    selected: true,
  })) as DomainWithAnalysis[]

  // Format URLs/pages (URLs with paths) - source_type = 'url'
  const urlsFromResult: DomainWithAnalysis[] = transformedUrls.map((u) => {
    const fullUrl = u.full_url || u.url || ''
    let hostname = u.domain || ''

    try {
      const urlObj = new URL(fullUrl.startsWith('http') ? fullUrl : `https://${fullUrl}`)
      hostname = urlObj.hostname.replace(/^www\./, '')
    } catch {
      hostname = fullUrl.replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0]
    }

    return {
      host: hostname,
      full_url: fullUrl,
      source_type: 'url' as const,
      total_clicks: u.total_clicks || 0,
      queries_count: u.queries_count || 0,
      average_position: u.average_position || 0,
      popularity: u.popularity || 'parsed',
      selected: true,
    }
  })

  // Return ALL sources - domains first, then URLs
  // NO deduplication - we want to keep domains and URLs separate
  const allSources = [...domainsFromResult, ...urlsFromResult]

  console.log(
    `📊 Formatted sources: ${domainsFromResult.length} domains + ${urlsFromResult.length} URLs = ${allSources.length} total`
  )

  return allSources
}
